<template>
  <div
    class="base-view-root"
    :class="[$attrs.class, { 'base-view-root--inline': inline }]"
  >
    <base-view-header
      v-if="title"
      :title="title"
      :has-tour="hasTour"
      :back="back"
      :inline="inline"
      @tour-view="$emit('tour-view')"
    >
      <template v-slot:title="props">
        <slot name="title" v-bind="props" />
      </template>
      <template v-slot:filters="props">
        <slot name="filters" v-bind="props" />
      </template>
      <template v-slot:actions="props">
        <slot name="actions" v-bind="props" />
      </template>
      <template v-slot:extension>
        <slot name="header-extension" />
      </template>
    </base-view-header>
    <div
      v-if="title && !inline"
      class="subnav-spacer"
      :class="{ 'subnav-spacer--with-extension': hasHeaderExtension }"
      aria-hidden="true"
    ></div>
    <v-container
      fluid
      class="pa-0"
      :class="mainViewClass"
    >
      <v-row no-gutters>
        <v-col
          v-if="dense"
          cols="12"
          class="base-view-content-col"
          :class="contentColClass"
        >
          <v-container>
            <slot></slot>
          </v-container>
          <base-error-alerts
          ></base-error-alerts>
        </v-col>
        <v-col
          v-else
          cols="12"
          class="base-view-content-col"
          :class="contentColClass"
        >
          <slot></slot>
          <base-error-alerts
          ></base-error-alerts>
        </v-col>
      </v-row>
    </v-container>
    <slot name="other"></slot>
  </div>
</template>

<script>
export default {
  name: 'BaseViewContainer',

  inheritAttrs: false,

  emits: ['tour-view'],

  props: {
    title: {
      type: String,
      default: '',
    },
    fullwidth: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    back: {
      type: [String, Function],
      default: '',
    },
    compactTop: {
      type: Boolean,
      default: false,
    },
    /**
     * When true (e.g. TaskView inside a dialog), skip full-page min-height /
     * subnav chrome so the host modal controls layout. Still uses page gray
     * (`.background`) so white modal chrome doesn't show around tab content.
     */
    embedded: {
      type: Boolean,
      default: false,
    },
    /**
     * Nested titled chrome (e.g. Company Contacts tab): keep the subnav in
     * document flow inside the pane instead of fixing it under the app bar.
     * Matches master, where BaseViewHeader was never position:fixed.
     */
    inline: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    hasTour() {
      return typeof this.$.vnode?.props?.onTourView === 'function';
    },

    hasHeaderExtension() {
      return !!this.$slots['header-extension'];
    },

    mainViewClass() {
      if (this.embedded) {
        // Same page gray as full TaskView — modal shell is otherwise a white v-card
        return {
          'main-view--embedded': true,
          background: true,
        };
      }
      return {
        'main-view': true,
        background: true,
        'main-view--with-subnav': !!this.title,
        'main-view--with-subnav-extension': !!(this.title && this.hasHeaderExtension),
        // Workflow / Template / Draft: fullwidth, no title — detail spacer is
        // inside the slot. Bare .main-view min-height 100vh−48 would stack with
        // that spacer and create a phantom page scrollbar on every tab.
        'main-view--content-sized': this.fullwidth && !this.title,
      };
    },

    contentColClass() {
      const classes = {};

      if (this.fullwidth || this.embedded) {
        classes['pa-0'] = true;
      } else if (this.compactTop) {
        classes['pt-0'] = true;
      } else {
        // Master used pt-8 between header and content. Fixed subnav + spacer
        // only replace the header height — without this, tables sit flush under
        // the subnav (Contacts, LinkedIn list views, Bot Grid, etc.).
        classes['pt-8'] = true;
      }

      if (!this.dense && !this.fullwidth && !this.embedded && this.$vuetify.display.smAndUp) {
        classes['px-16'] = true;
      }

      return classes;
    },
  },
};
</script>

<style scoped>
.base-view-content-col {
  position: relative;
}

/*
 * Do not force a viewport min-height on .main-view.
 * v-main already pads for the app bar; titled views also have an in-flow
 * .subnav-spacer (64 / 112). A 100vh-based min-height on top of that stacks
 * past the visible viewport and creates a phantom page scrollbar on list
 * pages (Workflows, Contacts, etc.). Page gray comes from .v-main.background.
 */
.main-view {
  min-height: 0 !important;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center bottom;
}

.main-view--with-subnav {
  min-height: 0 !important;
}

.main-view--with-subnav-extension {
  min-height: 0 !important;
}

.main-view--embedded {
  min-height: 0 !important;
}

.main-view--content-sized {
  min-height: 0 !important;
}
</style>
